import Box from '@mui/material/Box';
import {uuid} from '../../../utils/utils';
import React, {ReactElement, useState} from 'react';
import Stack from '@mui/material/Stack';
import {TextField} from '@mui/material';
import {PsfTemplateItem} from '../../../@types/psfTemplate';

interface PsfTemplateItemProps {
    factor: PsfTemplateItem;
    updateFactor: (item: PsfTemplateItem) => void;
    existingFactors: PsfTemplateItem[];
    readonly: boolean;
    textMinRows?: number;
    textMaxRows?: number;
}
export default function PsfTemplateItemEditor(props: PsfTemplateItemProps): ReactElement {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { factor, updateFactor, readonly, existingFactors, textMaxRows, textMinRows } = props;

    const [nameError, setNameError] = useState<string|null>(null);
    const [codeError, setCodeError] = useState<string|null>(null);
    const uuidRef = React.useRef(uuid());
    const [factorName, setFactorName] = React.useState<string>('');
    const [factorCode, setFactorCode] = React.useState<string>('');
    const [factorDescription, setFactorDescription] = React.useState<string>('');
    const [factorExample, setFactorExample] = React.useState<string>('');

    React.useEffect(() => {
        if (factor) {
            setFactorName(factor.name);
            setFactorCode(factor.code);
            setFactorDescription(factor.description);
            setFactorExample(factor.examples);
        }
    }, [factor]);

    return <Box sx={{ width: '100%', height: '100%', p: 1 }}>
        <Stack spacing={2} direction="column" justifyContent='space-evenly'>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, pr: 0 }}>
                <TextField
                    aria-label={`${factor.id}-name-label-${uuidRef.current}`}
                    type='text'
                    variant="standard"
                    size='small'
                    label='Name'
                    id={`${factor.id}-name-input-${uuidRef.current}`}
                    value={`${factorName ?? ''}`}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        setNameError(event.target.value === '' ? 'Factor name is required.' :
                            (existingFactors.find((factor) => factor.name === event.target.value) ?
                                'Factor name already exists.' : ''));
                        setFactorName(event.target.value)
                        updateFactor({...factor, name: event.target.value});
                    }}
                    disabled={readonly}
                    error={ nameError !== '' && nameError !== null }
                    helperText={nameError}
                    sx={{ width: '78%', marginRight: '10px'}}
                />
                <TextField
                    aria-label={`${factor.id}-code-label-${uuidRef.current}`}
                    type='text'
                    variant="standard"
                    size='small'
                    label='Code'
                    id={`${factor.id}-code-input-${uuidRef.current}`}
                    value={`${factorCode ?? ''}`}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        setCodeError(event.target.value === '' ? '' :
                            (existingFactors.find((factor) => factor.code === event.target.value) ?
                                'Factor code already exists.' : ''));
                        setFactorCode(event.target.value);
                        updateFactor({...factor, code: event.target.value});
                    }}
                    sx={{
                        '& input': {
                            border: 'none',
                        }
                    }}
                    disabled={readonly}
                    error={ codeError !== '' && codeError !== null }
                    helperText={codeError}
                />
            </Box>
            <TextField
                aria-label={`${factor.id}-description-label`}
                type='text'
                variant="outlined"
                size='small'
                label={'Description'}
                id={`${factor.id}-description-input-${uuidRef.current}`}
                value={`${factorDescription ?? ''}`}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement|HTMLInputElement>): void => {
                    setFactorDescription(event.target.value);
                    updateFactor({...factor, description: event.target.value});
                }}
                sx={{
                    '& input': {
                        border: 'none',
                    }
                }}
                disabled={readonly}
                multiline
                minRows={textMinRows ?? 2}
                maxRows={textMaxRows ?? 5}
                fullWidth
            />
            <TextField
                aria-label={`${factor.id}-example-label`}
                type='text'
                variant="outlined"
                size='small'
                label={'Example'}
                id={`${factor.id}-example-input-${uuidRef.current}`}
                value={`${factorExample ?? ''}`}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement|HTMLInputElement>): void => {
                    setFactorExample(event.target.value);
                    updateFactor({...factor, examples: event.target.value});
                }}
                sx={{
                    '& input': {
                        border: 'none',
                    }
                }}
                disabled={readonly}
                multiline
                minRows={textMinRows ?? 2}
                maxRows={textMaxRows ?? 5}
                fullWidth
            />
        </Stack>
    </Box>;
}